/**
 * API client for the student portal.
 *
 * Same contract as the manager panel's client (manager-panel/src/ui/api.js):
 *   ok   -> { success: true, data }
 *   fail -> { success: false, error: { code, message } }
 *
 * Deliberately exposes ONLY the endpoints a student/parent session is
 * allowed to reach. Every one of them is server-side scoped to the
 * caller's own person_id by assertCanAccessStudent (api/src/lib/
 * middleware.js), so this list is a convenience, not the security
 * boundary — but keeping it short means a page can never accidentally
 * call something that 403s.
 */
const BASE = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8787') + '/api/v1';

// Distinct from the manager panel's keys on purpose: a parent checking
// their child's marks on the office computer must not clobber (or
// inherit) the secretary's session in the same browser.
const TOKEN_KEY = 'mtzedu_student_token';
const USER_KEY = 'mtzedu_student_user';

export const session = {
  token: () => localStorage.getItem(TOKEN_KEY),
  user: () => {
    try { return JSON.parse(localStorage.getItem(USER_KEY) || 'null'); } catch { return null; }
  },
  save: (token, user) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },
};

// The login screen is read by pupils and parents; the API's English
// messages are for developers. Same wording as the manager panel.
// ACCOUNT_LOCKED is absent on purpose: its message is the lock reason
// the office typed, already in Arabic.
const AUTH_MESSAGES = {
  INVALID_CODE: 'الرمز غير صحيح، تأكد منه وحاول مرة أخرى',
  RATE_LIMIT_EXCEEDED: 'محاولات خاطئة كثيرة. انتظر ١٥ دقيقة ثم حاول مجدداً.',
  NOT_FOUND: 'لا يوجد حساب بهذا الاسم. تأكد من الاسم ومن يسجّل الدخول، أو راجع إدارة المدرسة.',
  SMS_PROVIDER_ERROR: 'تعذّر إرسال الرمز، حاول مرة أخرى.',
  // Only on the verify step does VALIDATION_ERROR mean "no live code".
  'login-verify:VALIDATION_ERROR': 'انتهت صلاحية الرمز، اضغط «تغيير الاسم» واطلب رمزاً جديداً.',
};
const authMessage = (path, code) =>
  AUTH_MESSAGES[`${path.split('/').pop()}:${code}`] || AUTH_MESSAGES[code];

async function request(path, { method = 'GET', body, auth = true } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (auth) {
    const t = session.token();
    if (t) headers.Authorization = `Bearer ${t}`;
  }

  let res;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (networkError) {
    const e = new Error('تعذّر الاتصال بالخادم');
    e.cause = networkError;
    throw e;
  }

  const payload = await res.json().catch(() => null);

  if (!res.ok || !payload || payload.success === false) {
    // A dead or rejected session drops the user back to login rather
    // than leaving every screen showing a vague failure. Only for calls
    // that CARRIED a session: a wrong code on the login form is also a
    // 401, and reloading there threw the user back to step 1 instead of
    // letting them type the code again.
    if (res.status === 401 && headers.Authorization) {
      session.clear();
      if (typeof window !== 'undefined') window.location.reload();
    }
    const code = payload?.error?.code;
    const err = new Error(
      (path.startsWith('/auth/') && authMessage(path, code))
      || payload?.error?.message
      || `فشل الطلب (${res.status})`
    );
    err.status = res.status;
    err.code = payload?.error?.code;
    throw err;
  }

  return payload.data;
}

const qs = (params = {}) => {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  return entries.length ? `?${new URLSearchParams(entries)}` : '';
};

const api = {
  // --- auth: name + (parent type) -> code -> 30-day token ------------
  loginLookup: (name, parent_type = 'none') =>
    request('/auth/login-lookup', { method: 'POST', body: { name, role: 'student', parent_type }, auth: false }),
  loginSelect: (user_id) =>
    request('/auth/login-select', { method: 'POST', body: { user_id }, auth: false }),
  loginVerify: (user_id, code, device_label) =>
    request('/auth/login-verify', { method: 'POST', body: { user_id, code, device_label }, auth: false }),
  logout: () => request('/auth/logout', { method: 'POST' }),
  // Welcome intro: the portal version this account last saw it for.
  introStatus: () => request('/auth/intro'),
  markIntroSeen: (version) => request('/auth/intro', { method: 'POST', body: { version } }),
  // The welcome version and/or the daily tour's progress: { version?, tips? }.
  saveIntro: (body) => request('/auth/intro', { method: 'POST', body }),

  // --- the pupils this login may see -------------------------------
  // A pupil: themself. A father/mother: every child of the family, each
  // with section_label and is_linked (the pupil the login points at).
  myStudents: () => request('/students'),
  academicYears: () => request('/academic-years'),

  // --- one pupil's data ---------------------------------------------
  // `id` must be one of myStudents(); the server rejects any other.
  student: (id) => request(`/students/${id}`),
  reportCard: (id) => request(`/students/${id}/report-card`),
  attendance: (id, params) => request(`/students/${id}/attendance${qs(params)}`),
  paymentSummary: (id) => request(`/students/${id}/payment-summary`),

  // --- own notification inbox (scoped to session.user_id server-side) -
  // Each row's student_id says which child it is about (null = general).
  notifications: (params) => request(`/notifications${qs(params)}`),
  markRead: (id) => request(`/notifications/${id}/read`, { method: 'PATCH' }),
  // `{ student_id }` → only that child's rows + general ones.
  markAllRead: (params) => request(`/notifications/mark-all-read${qs(params)}`, { method: 'PATCH' }),
};

export default api;
